import React from 'react';
import styled from 'styled-components';
import { Routes, Route, useNavigate, useLocation, useParams } from 'react-router';
import { Header } from '@app/navigation/header';
import { Body, Container, H5, Page } from '@ui';
import { BackArrowButton, FlatButton, SkeletonButton } from '@ui/buttons';
import { ROUTES } from '@constants/routes';
import { AI_TOOLS } from '@constants';
import { useCurrentUserToolsAvailable } from '@hooks/user/useCurrentUser';
import { SpecList } from '@features/scoreSpec/SpecList';
import { SpecEditor } from '@features/scoreSpec/SpecEditor';
import { SCORE_SPEC_HEADING_ID } from '@features/scoreSpec/headingId';

const PageWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

// Remount the editor when the spec id changes (keyed) so no state carries over between specs.
// Without this, navigating e.g. an approved spec -> "Duplicate as next version" (/specs/new) reuses
// the same instance: the old status pill lingers and the create-mode seed never re-initializes.
const KeyedSpecEditor = () => {
  const { specId } = useParams();
  return <SpecEditor key={specId} />;
};

// Score Spec authoring. Mounted at /advanced-score-spec/* (a splat) so it owns the nested list/editor
// sub-routes. Gated client-side on the score_spec_admin AI mode (the route is otherwise reachable
// by direct URL). Mirrors the ScoreBackfill page shell.
export const ScoreSpec = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const tools = useCurrentUserToolsAvailable();

  // Client-side hardening — redirect once tools have loaded and the grant is absent.
  React.useEffect(() => {
    if (tools && tools.length > 0 && !tools.includes(AI_TOOLS.SCORE_SPEC_ADMIN)) {
      navigate(ROUTES.home);
    }
  }, [tools, navigate]);

  const isList = location.pathname === ROUTES.scoreSpec || location.pathname === `${ROUTES.scoreSpec}/`;
  // The editor needs room for the Studio's three columns (prompt beside format_prompt beside the
  // declared shape). Below 1024px they stack in source order rather than disabling.
  const pageWidth = isList ? '1040px' : '1120px';

  // Move focus to the heading on every sub-view change (a11y).
  React.useEffect(() => {
    document.getElementById(SCORE_SPEC_HEADING_ID)?.focus();
  }, [location.pathname]);

  const onGoBack = () => navigate(isList ? ROUTES.home : ROUTES.scoreSpec);
  const backLabel = isList ? 'Back to home' : 'Back to specs';

  // The list's three entry points, hoisted out of SpecList and onto the back-arrow bar: they are
  // ways INTO this screen's work, so they belong in its chrome rather than in a row of their own
  // above the filters, where they pushed the table down and read as part of it.
  //
  // The simple modes. Most authoring here is a prompt revision on a spec that already works, and
  // that path does not need this editor — it needs three fields.
  //
  // The third of them, "Edit an approved spec", is not here: it lives at the bottom of the fetch
  // screen, under the record. Deciding a spec needs a new version is a reaction to reading it, and
  // a row click lands on that record anyway.
  //
  // Only on the list route — they act on the list, and the editor has its own actions.
  const listActions = [
    <SkeletonButton key="fetch" onClick={() => navigate(ROUTES.getScoreSpec)}>
      Fetch a spec by id
    </SkeletonButton>,
    <SkeletonButton key="paste" onClick={() => navigate(ROUTES.createScoreSpec)}>
      Paste a new spec
    </SkeletonButton>,
    <FlatButton key="create" onClick={() => navigate(`${ROUTES.scoreSpecSpecs}/new`)}>
      Create new spec
    </FlatButton>,
  ];

  return (
    <PageWrapper>
      <Header />
      <Body withFooter>
        <Page width={pageWidth}>
          <BackArrowButton onClick={onGoBack} aria-label={backLabel} actions={isList ? listActions : null} />
          {/* The action bar sits in the flow and is a button tall; the bare arrow floats over the
              page and needs the content cleared out from under it. */}
          <Container top={isList ? 24 : 48}>
            {/* On the editor routes the heading is rendered by SpecEditor, which is the only
                thing that knows whether this is a new draft, a named one, or a frozen record. */}
            {isList && (
              <H5 id={SCORE_SPEC_HEADING_ID} tabIndex={-1}>
                Score Specs
              </H5>
            )}
            <Container top={isList ? 24 : 0}>
              <Routes>
                <Route index element={<SpecList />} />
                <Route path="specs/:specId/*" element={<KeyedSpecEditor />} />
              </Routes>
            </Container>
          </Container>
        </Page>
      </Body>
    </PageWrapper>
  );
};

export default ScoreSpec;
